/**
 * CTWA Ads Management API Types
 */

// ============================================
// CTWA Mapping Types
// ============================================

export interface CTWAMappingData {
  id: string;
  cin: string;
  pageId: string;
  adId: string;
  adAccountId: string;
  createdBy: string;
  adPhoneNumber: string;
  createdTs: number;
  updatedBy: string;
  updatedTs: number;
  permissions: string;
  accountStatus: 'connected' | 'disconnected' | 'pending' | string;
}

export interface CTWAMappingResponse {
  status: boolean;
  message: string;
  data: CTWAMappingData;
}

export interface CTWAErrorResponse {
  status: boolean;
  message: string;
}

// ============================================
// Facebook Page Types (CTWA Setup)
// ============================================

/**
 * Category item in Facebook Page category list
 */
export interface FacebookPageCategory {
  id: string;
  name: string;
}

/**
 * Location data for Facebook Page
 */
export interface FacebookPageLocation {
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  street?: string;
  zip?: string;
}

/**
 * Picture data for Facebook Page
 */
export interface FacebookPagePicture {
  data: {
    height: number;
    is_silhouette: boolean;
    url: string;
    width: number;
  };
}

/**
 * Facebook Page data structure
 */
export interface FacebookPage {
  id: string;
  name: string;
  phone?: string;
  access_token?: string;
  category?: string;
  category_list?: FacebookPageCategory[];
  location?: FacebookPageLocation;
  picture?: FacebookPagePicture;
  website?: string;
}

/**
 * Response from list pages API
 */
export interface ListPagesResponse {
  data: FacebookPage[];
  paging?: {
    cursors?: {
      before?: string;
      after?: string;
    };
  };
}

// ============================================
// Ad Account Types (CTWA Setup)
// ============================================

/**
 * Facebook Ad Account status codes
 * 1 = Active, 2 = Disabled, 3 = Unsettled, etc.
 */
export type AdAccountStatus = 1 | 2 | 3 | 7 | 8 | 9 | 100 | 101 | 201 | 202 | number;

/**
 * Facebook Ad Account data structure
 */
export interface FacebookAdAccount {
  id: string;
  account_id: string;
  name: string;
  account_status: AdAccountStatus;
  currency: string;
  owner?: string;
  spend_cap?: string;
  amount_spent?: string;
  timezone_name?: string;
}

/**
 * Response from list ad accounts API
 */
export interface ListAdAccountsResponse {
  data: FacebookAdAccount[];
  paging?: {
    cursors?: {
      before?: string;
      after?: string;
    };
  };
}

// ============================================
// CTWA Mapping Create/Update Types
// ============================================

/**
 * Request payload for creating or updating CTWA mapping
 */
export interface CreateOrUpdateCTWAMappingRequest {
  /** Existing mapping ID (for updates, optional for new) */
  id?: string;
  /** Page ID from Facebook */
  pageId: string;
  /** Ad ID (can be same as ad account ID initially) */
  adId?: string;
  /** Ad Account ID */
  adAccountId: string;
  /** WhatsApp phone number linked to the page */
  adPhoneNumber: string;
  /** User who is making the update */
  updatedBy?: string;
  /** Permissions granted during Facebook authorization */
  permissions?: string;
  /** Account connection status */
  accountStatus?: 'connected' | 'disconnected' | 'pending';
}

/**
 * Response from create/update CTWA mapping API
 */
export interface CreateOrUpdateCTWAMappingResponse {
  status: boolean;
  message: string;
}

// ============================================
// Campaign Types
// ============================================

export interface Campaign {
  id: string;
  name: string;
  status: 'ACTIVE' | 'PAUSED' | 'DELETED' | 'ARCHIVED';
  objective: string;
  created_time: string;
  effective_status: string;
  daily_budget?: string;
  start_time: string;
  stop_time?: string;
  updated_time: string;
}

export interface CampaignListRequest {
  cin: string;
  adAccountId: string;
  fields: string[];
}

export interface CampaignListResponse {
  data: Campaign[];
  paging?: {
    cursors?: {
      before?: string;
      after?: string;
    };
  };
}

// ============================================
// Query Parameters
// ============================================

export interface GetCTWAMappingParams {
  cin: string;
}

export interface ListCampaignsParams {
  adAccountId?: string;
  fields?: string[];
}

// ============================================
// Campaign Creation Types
// ============================================

/**
 * Campaign objectives supported by Meta Ads API
 */
export type CampaignObjective =
  | 'OUTCOME_ENGAGEMENT'
  | 'OUTCOME_LEADS'
  | 'OUTCOME_SALES'
  | 'OUTCOME_AWARENESS'
  | 'OUTCOME_TRAFFIC'
  | 'OUTCOME_APP_PROMOTION';

/**
 * Campaign status options
 */
export type CampaignStatus = 'ACTIVE' | 'PAUSED';

/**
 * Special ad categories for compliance
 */
export type SpecialAdCategory =
  | 'CREDIT'
  | 'EMPLOYMENT'
  | 'HOUSING'
  | 'ISSUES_ELECTIONS_POLITICS'
  | 'ONLINE_GAMBLING_AND_GAMING';

/**
 * Request payload for creating a campaign
 */
export interface CreateCampaignRequest {
  /** Campaign name (user provided) */
  name: string;
  /** Campaign objective (default: OUTCOME_ENGAGEMENT) */
  objective?: CampaignObjective;
  /** Campaign status (default: ACTIVE) */
  status?: CampaignStatus;
  /** Special ad categories for compliance */
  special_ad_categories?: SpecialAdCategory[];
}

/**
 * Full payload sent to the API (internal use)
 */
export interface CreateCampaignPayload {
  cin: string;
  name: string;
  objective: CampaignObjective;
  status: CampaignStatus;
  ad_account_id: string;
  special_ad_categories: SpecialAdCategory[];
}

/**
 * Response from create campaign API
 */
export interface CreateCampaignResponse {
  id: string;
}

// ============================================
// Insights Types
// ============================================

export interface Insight {
  campaign_name?: string;
  campaign_id?: string;
  account_currency?: string;
  account_id?: string;
  [key: string]: unknown;
}

export interface InsightsRequest {
  cin: string;
  level: 'campaign' | 'adset' | 'ad' | 'account';
  objectId: string;
  fields: string[];
  datePreset?: string;
}

export interface InsightsResponse {
  data: Insight[];
  paging?: {
    cursors?: {
      before?: string;
      after?: string;
    };
  };
}

export interface GetInsightsParams {
  level: 'campaign' | 'adset' | 'ad' | 'account';
  objectId?: string;
  fields?: string[];
  datePreset?: string;
}

// ============================================
// Ad Types
// ============================================

export interface Ad {
  id: string;
  name: string;
  status: 'ACTIVE' | 'PAUSED' | 'DELETED' | 'ARCHIVED';
  campaign_id: string;
  adset_id?: string;
  creative?: {
    id: string;
    name?: string;
  };
  // Preview URL
  preview_shareable_link?: string;
  // Source tracking
  source_ad_id?: string;
  // Bidding
  bid_amount?: number;
  // Review feedback
  ad_review_feedback?: {
    global?: Record<string, string>;
    placement_specific?: Record<string, unknown>;
  };
  // Recommendations from Meta
  recommendations?: Array<{
    title?: string;
    message?: string;
    code?: number;
    importance?: string;
    confidence?: string;
  }>;
  // Configured status (user's intended status)
  configured_status?: 'ACTIVE' | 'PAUSED' | 'DELETED' | 'ARCHIVED';
  // Issues affecting this ad
  issues_info?: Array<{
    error_code?: number;
    error_message?: string;
    error_summary?: string;
    error_type?: string;
    level?: string;
  }>;
  // Labels for organization
  adlabels?: Array<{
    id: string;
    name: string;
  }>;
  // Tracking specs for conversions
  tracking_specs?: Array<Record<string, unknown>>;
  created_time?: string;
  updated_time?: string;
  effective_status?: string;
}

export interface AdsListResponse {
  data: Ad[];
  paging?: {
    cursors?: {
      before?: string;
      after?: string;
    };
  };
}

export interface ListAdsParams {
  adAccountId?: string;
  fields?: string[];
}

// ============================================
// AdSet Types
// ============================================

export interface AdSet {
  id: string;
  name: string;
  status: 'ACTIVE' | 'PAUSED' | 'DELETED' | 'ARCHIVED';
  campaign_id: string;
  // Budget fields
  daily_budget?: string;
  lifetime_budget?: string;
  budget_remaining?: string;
  // Schedule fields
  start_time?: string;
  end_time?: string;
  // Targeting & Optimization
  targeting?: AdSetTargeting;
  optimization_goal?: string;
  billing_event?: string;
  bid_amount?: number;
  bid_strategy?: string;
  // Additional metadata
  promoted_object?: Record<string, unknown>;
  destination_type?: string;
  created_time?: string;
  updated_time?: string;
  effective_status?: string;
}

export interface AdSetsListResponse {
  data: AdSet[];
  paging?: {
    cursors?: {
      before?: string;
      after?: string;
    };
  };
}

export interface ListAdSetsParams {
  adAccountId?: string;
  fields?: string[];
}

// ============================================
// Unified Campaign Resources Types
// ============================================

/**
 * Resource types that can be fetched from campaign-related services
 */
export type CampaignResourceType = 'ads' | 'adsets';

/**
 * Default fields for each resource type
 * Based on Facebook Marketing API reference:
 * https://developers.facebook.com/docs/marketing-api/reference/ad-campaign
 */
export const CAMPAIGN_RESOURCE_DEFAULT_FIELDS: Record<CampaignResourceType, string[]> = {
  ads: [
    // Basic identification
    'id',
    'name',
    'status',
    'effective_status',
    'configured_status',
    'campaign_id',
    'adset_id',
    // Creative & Preview
    'creative',
    'preview_shareable_link',
    // Source tracking
    'source_ad_id',
    // Bidding
    'bid_amount',
    // Review & Recommendations
    'ad_review_feedback',
    'recommendations',
    // Issues & Labels
    'issues_info',
    'adlabels',
    // Tracking
    'tracking_specs',
    // Timestamps
    'created_time',
    'updated_time',
  ],
  adsets: [
    // Basic identification
    'id',
    'name',
    'status',
    'effective_status',
    'campaign_id',
    // Budget & Schedule
    'daily_budget',
    'lifetime_budget',
    'budget_remaining',
    'start_time',
    'end_time',
    // Targeting & Optimization
    'targeting',
    'optimization_goal',
    'billing_event',
    'bid_amount',
    'bid_strategy',
    // Additional metadata
    'promoted_object',
    'destination_type',
    'created_time',
    'updated_time',
  ],
};

/**
 * Unified params for listing campaign resources (ads/adsets)
 */
export interface ListCampaignResourcesParams {
  /** Resource type to fetch */
  resourceType: CampaignResourceType;
  /** Optional ad account ID (defaults to store value) */
  adAccountId?: string;
  /** Optional fields to fetch (defaults based on resourceType) */
  fields?: string[];
  /** Optional campaign ID filter (for fetching adsets under a campaign) */
  campaignId?: string;
  /** Optional adset ID filter (for fetching ads under an adset) */
  adsetId?: string;
}

/**
 * Unified response for campaign resources
 */
export interface CampaignResourcesResponse<T = Ad | AdSet> {
  data: T[];
  paging?: {
    cursors?: {
      before?: string;
      after?: string;
    };
  };
}

// ============================================
// AdSet Creation Types
// ============================================

/**
 * Destination types for ad sets
 */
export type AdSetDestinationType = 'WHATSAPP' | 'MESSENGER' | 'INSTAGRAM_DIRECT' | 'WEBSITE';

/**
 * Optimization goals for ad sets
 */
export type AdSetOptimizationGoal =
  | 'IMPRESSIONS'
  | 'REACH'
  | 'LINK_CLICKS'
  | 'LANDING_PAGE_VIEWS'
  | 'CONVERSATIONS'
  | 'LEAD_GENERATION';

/**
 * Billing events for ad sets
 */
export type AdSetBillingEvent = 'IMPRESSIONS' | 'LINK_CLICKS' | 'PAGE_LIKES' | 'POST_ENGAGEMENT';

/**
 * Bid strategies for ad sets
 */
export type AdSetBidStrategy =
  | 'LOWEST_COST_WITHOUT_CAP'
  | 'LOWEST_COST_WITH_BID_CAP'
  | 'COST_CAP';

/**
 * Targeting configuration for ad sets
 */
export interface AdSetTargeting {
  geo_locations?: {
    countries?: string[];
    regions?: Array<{
      key: string;
      name?: string;
      country?: string;
    }>;
    cities?: Array<{
      key: string;
      name?: string;
      radius?: number;
      distance_unit?: string;
      country?: string;
      country_code?: string;
      country_name?: string;
      region?: string;
      region_id?: number | string;
      supports_region?: boolean;
      supports_city?: boolean;
      primary_city?: string;
      primary_city_id?: number;
    }>;
    zips?: Array<{
      key: string;
      name?: string;
    }>;
    location_types?: string[];
  };
  age_min?: number;
  age_max?: number;
  genders?: number[];
  interests?: Array<{ id: string; name: string }>;
  behaviors?: Array<{ id: string; name: string }>;
  custom_audiences?: Array<{ id: string }>;
  excluded_custom_audiences?: Array<{ id: string }>;
  flexible_spec?: Array<{
    interests?: Array<{ id: string; name: string }>;
    behaviors?: Array<{ id: string; name: string }>;
    industries?: Array<{ id: string; name: string }>;
    work_employers?: Array<{ id: string; name: string }>;
    [key: string]: unknown;
  }>;
  exclusions?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Promoted object for ad sets (e.g., page_id for CTWA)
 */
export interface AdSetPromotedObject {
  page_id: string;
  pixel_id?: string;
  application_id?: string;
  object_store_url?: string;
}

/**
 * Request payload for creating an ad set (user-facing)
 */
export interface CreateAdSetRequest {
  /** Campaign ID to link this ad set to (required) */
  campaign_id: string;
  /** Ad set name (required) */
  name: string;
  /** Targeting configuration */
  targeting?: AdSetTargeting;
  /** Start time in ISO 8601 format (required) */
  start_time: string;
  /** End time in ISO 8601 format (required) */
  end_time: string;
  /** Daily budget in cents (default: 50000 = $500.00) */
  daily_budget?: number;
  /** Ad set status (default: ACTIVE) */
  status?: CampaignStatus;
}

/**
 * Full payload sent to the API (internal use)
 */
export interface CreateAdSetPayload {
  cin: string;
  ad_account_id: string;
  name: string;
  status: CampaignStatus;
  targeting: AdSetTargeting;
  daily_budget: number;
  destination_type: AdSetDestinationType;
  optimization_goal: AdSetOptimizationGoal;
  promoted_object: AdSetPromotedObject;
  start_time: string;
  end_time: string;
  billing_event: AdSetBillingEvent;
  bid_strategy: AdSetBidStrategy;
  campaign_id: string;
}

/**
 * Response from create ad set API
 */
export interface CreateAdSetResponse {
  id: string;
}

// ============================================
// Target Search Types (Location/Geo Search)
// ============================================

export type LocationType =
  | 'country'
  | 'region'
  | 'city'
  | 'zip'
  | 'geo_market'
  | 'electoral_district';

export type TargetSearchType =
  | 'adgeolocation'
  | 'adinterest'
  | 'adlocale'
  | 'adworkemployer'
  | 'adworkposition'
  | 'adeducationschool'
  | 'adeducationmajor';

/**
 * Target search types that require 'q' parameter for searching
 */
export const TARGET_TYPES_REQUIRING_QUERY = [
  'adlocale',
  'adeducationmajor',
  'adeducationschool',
  'adworkemployer',
  'adworkposition',
] as const;

export type QueryBasedTargetType = typeof TARGET_TYPES_REQUIRING_QUERY[number];

/**
 * Targeting category classes for adTargetingCategory endpoint
 * These return full data without requiring a search query
 */
export const TARGETING_CATEGORY_CLASSES = [
  'interests',
  'behaviors',
  'demographics',
  'life_events',
  'industries',
  'income',
  'family_statuses',
  'user_device',
  'user_os',
] as const;

export type TargetingCategoryClass = typeof TARGETING_CATEGORY_CLASSES[number];

/**
 * Parameters for fetching targeting categories
 */
export interface GetTargetingCategoryParams {
  /** Category class to fetch */
  class: TargetingCategoryClass;
  /** Skip cache and force fresh fetch */
  skipCache?: boolean;
}

/**
 * Targeting category item structure
 */
export interface TargetingCategoryItem {
  id: string;
  name: string;
  type?: string;
  path?: string[];
  description?: string;
  audience_size_lower_bound?: number;
  audience_size_upper_bound?: number;
  /** Original key if present */
  key?: string | number;
}

/**
 * Response for targeting category fetch
 */
export interface TargetingCategoryResponse {
  status: boolean;
  message: string;
  data: TargetingCategoryItem[];
}

/**
 * Generic target search result for non-geolocation searches
 */
export interface GenericTargetResult {
  id: string;
  name: string;
  /** Key field - used by some endpoints (e.g., locales) instead of id */
  key?: string | number;
  coverage_lower_bound?: number;
  coverage_upper_bound?: number;
  type?: string;
  path?: string[];
  description?: string;
  audience_size_lower_bound?: number;
  audience_size_upper_bound?: number;
  topic?: string;
  disambiguation_category?: string;
}

/**
 * Response structure for generic target search
 */
export interface GenericTargetSearchResponse {
  status: boolean;
  message: string;
  data: GenericTargetResult[];
}

/**
 * Cached target data structure for IndexedDB storage
 */
export interface CachedTargetData {
  type: TargetSearchType;
  query: string;
  data: GenericTargetResult[];
  timestamp: number;
  expiresAt: number;
}

/**
 * Cached category data structure for IndexedDB storage
 */
export interface CachedCategoryData {
  categoryClass: TargetingCategoryClass;
  data: TargetingCategoryItem[];
  timestamp: number;
  expiresAt: number;
}

/**
 * Target search cache configuration
 */
export const TARGET_CACHE_CONFIG = {
  /** Cache duration in milliseconds (24 hours) */
  CACHE_DURATION_MS: 24 * 60 * 60 * 1000,
  /** Category cache duration (7 days - data changes less frequently) */
  CATEGORY_CACHE_DURATION_MS: 7 * 24 * 60 * 60 * 1000,
  /** IndexedDB database name */
  DB_NAME: 'notifyy_target_cache',
  /** IndexedDB store name */
  STORE_NAME: 'targets',
  /** Category store name */
  CATEGORY_STORE_NAME: 'targeting_categories',
  /** IndexedDB version - increment when schema changes */
  DB_VERSION: 2,
  /** Maximum cache entries per type */
  MAX_ENTRIES_PER_TYPE: 100,
} as const;

/**
 * Individual location/target result
 */
export interface TargetSearchResult {
  key: string;
  name: string;
  type: string;
  country_code?: string;
  country_name?: string;
  region?: string;
  region_id?: number;
  primary_city?: string;
  primary_city_id?: number;
  supports_region?: boolean;
  supports_city?: boolean;
}

/**
 * Parsed data structure from API response
 */
export interface TargetSearchData {
  data: TargetSearchResult[];
}

/**
 * Raw API response (data comes as stringified JSON)
 */
export interface TargetSearchRawResponse {
  status: boolean;
  message: string;
  data: string; // Stringified JSON that needs to be parsed
}

/**
 * Parsed API response
 */
export interface TargetSearchResponse {
  status: boolean;
  message: string;
  data: TargetSearchResult[];
}

/**
 * Parameters for target search API
 */
export interface TargetSearchParams {
  /** Search query entered by user */
  q: string;
  /** Type of search (default: adgeolocation) */
  type?: TargetSearchType;
  /** Location types to search (for adgeolocation) */
  locationTypes?: LocationType[];
  /** Maximum number of results (default: 1000) */
  limit?: number;
  /** Match country code filter */
  matchCountryCode?: boolean;
}

// ============================================
// Ad Creative Types
// ============================================

/**
 * Link data for ad creative (used in object_story_spec)
 */
export interface AdCreativeLinkData {
  /** Call to action button configuration */
  call_to_action?: {
    type: string;
    value?: {
      link?: string;
      whatsapp_number?: string;
      app_destination?: string;
      [key: string]: unknown;
    };
  };
  /** Description text */
  description?: string;
  /** Link URL */
  link?: string;
  /** Message text for the ad */
  message?: string;
  /** Name/title of the ad */
  name?: string;
  /** Image hash for the ad creative */
  image_hash?: string;
  /** Picture URL */
  picture?: string;
  /** Caption for the ad */
  caption?: string;
  /** Child attachments for carousel ads */
  child_attachments?: Array<{
    link?: string;
    image_hash?: string;
    name?: string;
    description?: string;
    call_to_action?: {
      type: string;
      value?: Record<string, unknown>;
    };
    [key: string]: unknown;
  }>;
  /** Multi-share end card */
  multi_share_end_card?: boolean;
  /** Allow extensible properties */
  [key: string]: unknown;
}

/**
 * Video data for ad creative
 */
export interface AdCreativeVideoData {
  /** Video ID */
  video_id?: string;
  /** Image URL for video thumbnail */
  image_url?: string;
  /** Image hash for video thumbnail */
  image_hash?: string;
  /** Title of the video ad */
  title?: string;
  /** Message/description */
  message?: string;
  /** Call to action */
  call_to_action?: {
    type: string;
    value?: Record<string, unknown>;
  };
  /** Allow extensible properties */
  [key: string]: unknown;
}

/**
 * Object story spec - defines the creative content of the ad
 */
export interface ObjectStorySpec {
  /** Page ID that owns the ad */
  page_id?: string;
  /** Instagram actor ID (for Instagram placements) */
  instagram_actor_id?: string;
  /** Link data for link ads */
  link_data?: AdCreativeLinkData;
  /** Video data for video ads */
  video_data?: AdCreativeVideoData;
  /** Photo data for photo ads */
  photo_data?: {
    image_hash?: string;
    url?: string;
    caption?: string;
    [key: string]: unknown;
  };
  /** Text data for text-based ads */
  text_data?: {
    message?: string;
    [key: string]: unknown;
  };
  /** Template data for dynamic ads */
  template_data?: Record<string, unknown>;
  /** Allow extensible properties */
  [key: string]: unknown;
}

/**
 * Creative features for degrees of freedom spec
 */
export interface CreativeFeatures {
  /** Standard enhancements configuration */
  standard_enhancements?: {
    /** Whether to use enhancements globally */
    enroll_status?: 'OPT_IN' | 'OPT_OUT';
  };
  /** Allow extensible properties */
  [key: string]: unknown;
}

/**
 * Degrees of freedom spec - allows Meta to optimize creative elements
 */
export interface DegreesOfFreedomSpec {
  /** Creative features configuration */
  creative_features_spec?: CreativeFeatures;
  /** Allow extensible properties */
  [key: string]: unknown;
}

/**
 * Request payload for creating an ad creative (user-facing)
 */
export interface CreateAdCreativeRequest {
  /** Name of the ad creative (required) */
  name: string;
  /** Object story spec - defines the ad content */
  object_story_spec?: ObjectStorySpec;
  /** Degrees of freedom spec - allows Meta to optimize */
  degrees_of_freedom_spec?: DegreesOfFreedomSpec;
}

/**
 * Full payload sent to the API (internal use)
 */
export interface CreateAdCreativePayload {
  cin: string;
  name: string;
  ad_account_id: string;
  object_story_spec: ObjectStorySpec;
  degrees_of_freedom_spec: DegreesOfFreedomSpec;
}

/**
 * Response from create ad creative API
 */
export interface CreateAdCreativeResponse {
  id: string;
}

// ============================================
// Ad Creation Types
// ============================================

/**
 * Call to action value for WhatsApp ads
 */
export interface AdCallToActionValue {
  app_destination: 'WHATSAPP';
}

/**
 * Call to action configuration for link_data
 */
export interface AdCallToAction {
  type: 'WHATSAPP_MESSAGE';
  value: AdCallToActionValue;
}

/**
 * Link data structure for ad creative
 */
export interface AdLinkData {
  /** Ad headline - displayed prominently */
  name: string;
  /** Ad body text - main message */
  message: string;
  /** Description - optional, typically empty for CTWA */
  description: string;
  /** Image hash from ad media upload API */
  image_hash: string;
  /** WhatsApp link - hardcoded */
  link: string;
  /** Call to action configuration */
  call_to_action: AdCallToAction;
}

/**
 * Object story spec for ad creative
 */
export interface AdCreativeObjectStorySpec {
  /** Facebook Page ID from CTWA mapping */
  page_id: string;
  /** Link data containing ad content */
  link_data: AdLinkData;
}

/**
 * Creative structure for ad creation
 */
export interface CreateAdCreative {
  /** Creative name - same as ad name */
  name: string;
  /** Object story spec with page and link data */
  object_story_spec: AdCreativeObjectStorySpec;
}

/**
 * Request payload for creating an ad (user-facing)
 * Based on ctwa-target.md specification
 */
export interface CreateAdRequest {
  /** Ad name */
  name: string;
  /** AdSet ID to link this ad to (required) */
  adset_id: string;
  /** Ad headline - displayed prominently */
  headline: string;
  /** Ad body text - main message */
  body: string;
  /** Image hash from ad media upload API */
  image_hash: string;
  /** Ad status (default: ACTIVE) */
  status?: CampaignStatus;
}

/**
 * Full payload sent to the API (internal use)
 */
export interface CreateAdPayload {
  cin: string;
  name: string;
  creative: CreateAdCreative;
  status: CampaignStatus;
  ad_account_id: string;
  adset_id: string;
}

/**
 * Response from create ad API
 */
export interface CreateAdResponse {
  id: string;
}

/**
 * Hardcoded values for Ad creation
 */
export const AD_CREATION_CONSTANTS = {
  STATUS: 'ACTIVE' as const,
  LINK: 'https://api.whatsapp.com/send',
  CALL_TO_ACTION_TYPE: 'WHATSAPP_MESSAGE' as const,
  APP_DESTINATION: 'WHATSAPP' as const,
  DESCRIPTION: '',
} as const;

// ============================================
// Ad Media Upload Types
// ============================================

/**
 * Image data returned from ad media upload
 * The response contains detailed image information including multiple URL sizes
 */
export interface AdMediaUploadImageData {
  /** Hash identifier for the image */
  hash: string;
  /** Height of the uploaded image in pixels */
  height: number;
  /** Width of the uploaded image in pixels */
  width: number;
  /** Full CDN URL of the uploaded image */
  url: string;
  /** Name/identifier of the uploaded image (usually filename) */
  name: string;
  /** 128px thumbnail URL */
  url_128?: string;
  /** 256px thumbnail URL */
  url_256?: string;
  /** 256px thumbnail height */
  url_256_height?: string;
  /** 256px thumbnail width */
  url_256_width?: string;
}

/**
 * Response from ad media upload API
 * The images object uses the filename as the key
 */
export interface AdMediaUploadResponse {
  images: Record<string, AdMediaUploadImageData>;
}

/**
 * Request parameters for ad media upload
 */
export interface UploadAdMediaRequest {
  /** The file to upload (binary) */
  file: File;
}

// ============================================
// Ad Video Upload Types
// ============================================

/**
 * Video data returned from ad video upload
 * The response contains video information including video_id for ad creation
 */
export interface AdVideoUploadVideoData {
  /** Video ID for use in ad creation */
  id: string;
  /** Video title/name */
  title?: string;
  /** Video length in seconds */
  length?: number;
  /** Video width in pixels */
  width?: number;
  /** Video height in pixels */
  height?: number;
  /** Video thumbnail URL */
  picture?: string;
  /** Video status */
  status?: string;
  /** Created time */
  created_time?: string;
  /** Updated time */
  updated_time?: string;
}

/**
 * Response from ad video upload API
 * Returns the video object with its ID
 */
export interface AdVideoUploadResponse {
  /** Video ID for use in ad creative */
  id: string;
  /** Video title */
  title?: string;
  /** Video length in seconds */
  length?: number;
  /** Status of the video processing */
  status?: string;
  /** Thumbnail image URL */
  picture?: string;
}

/**
 * Request parameters for ad video upload
 */
export interface UploadAdVideoRequest {
  /** The video file to upload (binary) */
  file: File;
}

// ============================================
// Facebook OAuth Types
// ============================================

/**
 * Request parameters for FB access token exchange
 */
export interface FBAccessTokenExchangeRequest {
  /** Facebook short-lived access token from FB.login() */
  fbExchangeToken: string;
}

/**
 * Response from FB access token exchange API
 */
export interface FBAccessTokenExchangeResponse {
  status: boolean;
  message: string;
  data?: {
    access_token?: string;
    token_type?: string;
    expires_in?: number;
  };
}

/**
 * Facebook SDK configuration
 */
export const FB_SDK_CONFIG = {
  /** Facebook App ID for CTWA */
  APP_ID: '1203854810408944',
  /** Facebook Graph API version */
  VERSION: 'v23.0',
  /** Facebook OAuth permissions for CTWA */
  SCOPE: 'email,ads_management,pages_manage_ads,pages_read_engagement,pages_show_list,catalog_management,pages_manage_cta,business_management,read_insights',
} as const;

// ============================================
// Delete Campaign/AdSet/Ad Types
// ============================================

/**
 * Object types that can be deleted
 */
export type DeleteObjectType = 'campaign' | 'adset' | 'ad';

/**
 * Request payload for deleting campaign/adset/ad
 */
export interface DeleteObjectRequest {
  /** The ID of the object to delete */
  objectId: string;
  /** The type of object being deleted */
  objectType: DeleteObjectType;
}

/**
 * Full payload sent to the API (internal use)
 */
export interface DeleteObjectPayload {
  cin: string;
  objectId: string;
  objectType: DeleteObjectType;
}

/**
 * Response from delete API
 */
export interface DeleteObjectResponse {
  success: boolean;
  message?: string;
}

// ============================================
// Update Campaign Types
// ============================================

/**
 * Campaign status options for update (includes ARCHIVED)
 */
export type UpdateCampaignStatus = 'ACTIVE' | 'PAUSED' | 'ARCHIVED';

/**
 * Request payload for updating a campaign (user-facing)
 */
export interface UpdateCampaignRequest {
  /** Campaign ID to update (required) */
  campaignId: string;
  /** Campaign name (required for update) */
  name: string;
  /** Campaign objective (required for update) */
  objective: CampaignObjective;
  /** Campaign status (ACTIVE, PAUSED, or ARCHIVED) */
  status: UpdateCampaignStatus;
  /** Special ad categories for compliance */
  special_ad_categories?: SpecialAdCategory[];
}

/**
 * Full payload sent to the API (internal use)
 */
export interface UpdateCampaignPayload {
  cin: string;
  name: string;
  objective: CampaignObjective;
  status: UpdateCampaignStatus;
  ad_account_id: string;
  special_ad_categories: SpecialAdCategory[];
}

/**
 * Response from update campaign API
 */
export interface UpdateCampaignResponse {
  success: boolean;
}

// ============================================
// Update AdSet Types
// ============================================

/**
 * AdSet status options for update
 */
export type UpdateAdSetStatus = 'ACTIVE' | 'PAUSED' | 'ARCHIVED';

/**
 * Request payload for updating an ad set (user-facing)
 */
export interface UpdateAdSetRequest {
  /** AdSet ID to update (required) */
  adsetId: string;
  /** AdSet name (required) */
  name: string;
  /** AdSet status */
  status: UpdateAdSetStatus;
  /** Targeting configuration */
  targeting?: AdSetTargeting;
  /** Daily budget in cents */
  daily_budget?: string;
  /** Lifetime budget in cents (usually "0" for daily budget) */
  lifetime_budget?: string;
  /** Optimization goal */
  optimization_goal?: string;
  /** Bid strategy */
  bid_strategy?: string;
  /** Start time in ISO format */
  start_time?: string;
  /** End time in ISO format */
  end_time?: string;
}

/**
 * Full payload sent to the API (internal use)
 */
export interface UpdateAdSetPayload {
  cin: string;
  name: string;
  status: UpdateAdSetStatus;
  targeting: AdSetTargeting;
  daily_budget: string;
  lifetime_budget: string;
  optimization_goal: string;
  bid_strategy: string;
}

/**
 * Response from update ad set API
 */
export interface UpdateAdSetResponse {
  success: boolean;
}
